import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from "@angular/core";
import { PendingMessageStatus } from "@nx-fe/pending-message";
import { GlobalEventBusService } from "@nx-fe/services";
import { GlobalEvents } from "app/core/global-event-constants";
import { DeviceContext } from "app/modules/remote-control-ng/context/deviceContext";
import { RcContext } from "app/modules/remote-control-ng/context/rcContext";
import { IMonitorInfo } from "app/modules/remote-control-ng/definitions";
import { MonitorInfo } from "app/modules/remote-control-ng/sysInfo";
import { Subscription } from "rxjs";
import { XsI18nPipe } from "@nx-fe/pipes";
import { RemoteControlDeviceState } from "app/modules/remote-control/services/device-state-registry.service";

interface Rectangle {
  id: number
  top: number;
  left: number;
  width: number;
  height: number;
  name: string;
}

export interface VisualSelectionInfo {
  inVisualMode: boolean;
  monitorInfo?: MonitorInfo;
}

@Component({
  selector: 'xsight-multi-selection-view',
  templateUrl: './multi-display-selection-view.component.html',
  styleUrls: ['./multi-display-selection-view.component.scss'],
})
export class MultiDisplaySelectionViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() rcState: RemoteControlDeviceState;
  @Input() canvasWidth: number = 0;
  @Input() canvasHeight: number = 0;
  @Input() screenWidth: number = 0;
  @Input() screenHeight: number = 0;
  @Input() screenLeft: number = 0;
  @Input() screenTop: number = 0;
  @Input() availableDisplays: IMonitorInfo[];

  @Output() isInVisualSelectionMode = new EventEmitter<VisualSelectionInfo>();  // only used for close

  private inVisualSelectionMode = false;
  private visualSelectionSub: Subscription;
  private _rects: Rectangle[] = [];

  public alertConfig = {
    status: PendingMessageStatus.Info,
    message: this.saI18nPipe.transform('remoteControl_MultiScreen_InactiveSelection'),
  };

  constructor(
    private globalEventBusService: GlobalEventBusService,
    private saI18nPipe: XsI18nPipe
  ) {
  }

  ngOnInit(): void {
    this.visualSelectionSub = this.globalEventBusService.register(
      GlobalEvents.remoteControlMultiScreenVisualSelection,
      (isVisual: boolean) => {
        this.inVisualSelectionMode = isVisual;
        if (this.supportsVisualSelection()) {
          this.updateMask();
          this.generateRectangles();
        }
      }
    );
  }

  ngOnDestroy(): void {
    if (this.visualSelectionSub) {
      this.visualSelectionSub.unsubscribe();
    }

    this.close();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.supportsVisualSelection() && (changes.rcState|| changes.canvasWidth || changes.canvasHeight || changes.screenWidth || changes.screenHeight || changes.screenLeft || changes.screenTop)) {
      this.updateMask();
      this.generateRectangles();
    } else if (this.inVisualSelectionMode && !this.supportsVisualSelection()) {
      this.close();
    }
  }

  public close = () => {
    this.inVisualSelectionMode = false;

    if (this.availableDisplays.length > 0) {
      const previousMonitor = this.availableDisplays.find(d => d.id === RcContext.instance.multiScreen.savedMonitor);
      this.isInVisualSelectionMode.emit({ inVisualMode: false, monitorInfo: previousMonitor ? previousMonitor : this.availableDisplays[0] });
    }
  }

  public backToVirtual = () => {
    this.inVisualSelectionMode = false;

    if (this.availableDisplays.length > 0) {
      this.isInVisualSelectionMode.emit({ inVisualMode: false, monitorInfo: this.availableDisplays[0] });
    }
  }

  public get rectangles() {
    if (!this.supportsVisualSelection()) {
      return [];
    }
    return this._rects;
  }

  public get isDarkMode(): boolean {
    return localStorage.getItem('XSightTheme') === 'darkMode';
  }

  public click = (rect: Rectangle) => {
    const monitor = RcContext.instance.multiScreen.monitorList.find(d => d.id === rect.id);
    this.inVisualSelectionMode = false;
    this.isInVisualSelectionMode.emit({
      inVisualMode: this.inVisualSelectionMode,
      monitorInfo: monitor
    });
  }

  private updateMask = () => {
    const maskElement = document.getElementById("multi-display-visual-selection");
    const width = Math.min(100, Math.max(0, this.screenWidth / this.canvasWidth * 100));
    const height = Math.min(100, Math.max(0, this.screenHeight / this.canvasHeight * 100));
    const top = Math.min(100, Math.max(0, this.screenTop / this.canvasHeight * 100));
    const left = Math.min(100, Math.max(0, this.screenLeft / this.canvasWidth * 100));

    const maskSize = `${width}% ${top}%,` +
                      `${width}% ${100 - top - height}%, ` +
                      `${left}% ${100}%, ` +
                      `${100 - left - width}% ${100}%`;
    maskElement.style.maskSize = maskSize;
  }

  private supportsVisualSelection = (): boolean => {
    return RcContext.instance.multiScreen.monitorList?.length > 1
      && RcContext.instance.multiScreen.supportWinMonitorList
      && this.rcState && this.rcState.connected
      && this.inVisualSelectionMode;
  }

  private generateRectangles = () => {
    const ctx = RcContext.instance.deviceContext as DeviceContext;
    const monitorList = RcContext.instance.multiScreen.monitorList;

    this._rects = [];
    for (let i = 1; i < monitorList.length; i++) {
      const rect = {
        id: i,
        top: this.screenTop + monitorList[i].top * ctx.scale,
        left: this.screenLeft + monitorList[i].left * ctx.scale,
        width: monitorList[i].width * ctx.scale,
        height: monitorList[i].height * ctx.scale,
        name: this.saI18nPipe.transform('remoteControl_MultiScreen_Monitor') + " " + i
      };
      this._rects.push(rect);
    }
  }
}
